import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight, Trash2, Layers, Database } from 'lucide-react';
import { Button, InputField, SearchBar } from 'equal-ds-ui';
import { analyticsOnboardingAPI } from '../../../services/analytics-onboarding-api.service';
import type { AnalyticsAttributesResponse, AnalyticsAttribute } from '../../../types/analytics-onboarding';

interface SelectedAttributes {
  overall_analysis: Record<string, Record<string, { analytics_attributes: AnalyticsAttribute[] }>>;
  account_wise_analysis: Record<string, Record<string, { analytics_attributes: AnalyticsAttribute[] }>>;
  [key: string]: any;
}

interface AttributeSelectorProps {
  onSelectionChange: (config: SelectedAttributes) => void;
  disabled?: boolean;
  clientName?: string;
  instanceId?: string;
  /** Pre-existing analytics_attributes_configuration to pre-select */
  initialConfig?: any;
}

/* ── Helpers ── */

const getAttrs = (node: any): AnalyticsAttribute[] | null =>
  node?.analytics_attributes || node?.attributes || null;

const getAllPaths = (attrs: AnalyticsAttribute[] | null, basePath: string): string[] => {
  if (!attrs) return [];
  const paths: string[] = [];
  for (const a of attrs) {
    const p = `${basePath}.${a.name}`;
    paths.push(p);
    if (a.attributes) paths.push(...getAllPaths(a.attributes, p));
  }
  return paths;
};

const filterSelected = (
  attrs: AnalyticsAttribute[] | null,
  selected: Set<string>,
  basePath: string,
  customs?: Map<string, AnalyticsAttribute[]>,
): AnalyticsAttribute[] => {
  if (!attrs) return [];
  const all = [...attrs];
  if (customs?.has(basePath)) all.push(...customs.get(basePath)!);

  const result: AnalyticsAttribute[] = [];
  for (const a of all) {
    const p = `${basePath}.${a.name}`;
    const isSelected = selected.has(p);
    let children: AnalyticsAttribute[] = [];
    if (a.attributes) children = filterSelected(a.attributes, selected, p, customs);
    if (isSelected || children.length > 0) {
      result.push({
        name: a.name, type: a.type, attributes: children.length > 0 ? children : null,
        ...(a.custom_attribute_logic ? { custom_attribute_logic: a.custom_attribute_logic } : {}),
        ...(a.definition ? { definition: a.definition } : {}),
      });
    }
  }
  return result;
};

/**
 * Wildcard entry for "all attributes in a scope"
 */
const WILDCARD_ATTR = { name: '*', type: 'value' };

/**
 * Build all 4 scopes with wildcard for "all scopes, all attributes"
 */
const allScopesWildcard = () => ({
  overall: { analytics_attributes: [WILDCARD_ATTR] },
  monthly: { analytics_attributes: [WILDCARD_ATTR] },
  weekly: { analytics_attributes: [WILDCARD_ATTR] },
  daily: { analytics_attributes: [WILDCARD_ATTR] },
});

const buildConfig = (
  allAttrs: AnalyticsAttributesResponse,
  selected: Set<string>,
  customs: Map<string, AnalyticsAttribute[]>,
): SelectedAttributes => {
  const result: any = {};

  // Check if literally everything is selected across all sections
  let globalAllSelected = true;
  let globalAnySelected = false;
  for (const [analysisKey, analysisData] of Object.entries(allAttrs)) {
    if (!analysisData || typeof analysisData !== 'object') continue;
    for (const [section, sectionData] of Object.entries(analysisData as any)) {
      if (!sectionData || typeof sectionData !== 'object') continue;
      for (const [tf, tfData] of Object.entries(sectionData as any)) {
        const attrs = getAttrs(tfData);
        if (!attrs) continue;
        const basePath = `${analysisKey}.${section}.${tf}`;
        getAllPaths(attrs, basePath).forEach(p => {
          if (selected.has(p)) globalAnySelected = true;
          else globalAllSelected = false;
        });
      }
    }
  }

  // Also check if any custom attributes exist
  const hasAnyCustom = customs.size > 0;
  if (!globalAnySelected && !hasAnyCustom) return result;

  // If ALL standard attributes selected and no custom attributes → use "*" module wildcard
  if (globalAllSelected && !hasAnyCustom) {
    return { '*': allScopesWildcard() } as any;
  }

  for (const [analysisKey, analysisData] of Object.entries(allAttrs)) {
    if (!analysisData || typeof analysisData !== 'object') continue;

    // Check module-level selection (standard + custom)
    let moduleAnySelected = false;
    for (const [section, sectionData] of Object.entries(analysisData as any)) {
      if (!sectionData || typeof sectionData !== 'object') continue;
      for (const [tf, tfData] of Object.entries(sectionData as any)) {
        const attrs = getAttrs(tfData);
        if (!attrs) continue;
        const basePath = `${analysisKey}.${section}.${tf}`;
        getAllPaths(attrs, basePath).forEach(p => {
          if (selected.has(p)) moduleAnySelected = true;
        });
        // Check if customs exist for this path
        if (customs.has(basePath)) moduleAnySelected = true;
      }
    }

    if (!moduleAnySelected) continue;

    result[analysisKey] = {};

    for (const [section, sectionData] of Object.entries(analysisData as any)) {
      if (!sectionData || typeof sectionData !== 'object') continue;

      // Check category-level selection (standard + custom)
      let catAllSelected = true;
      let catAnySelected = false;
      const catScopes: string[] = [];
      for (const [tf, tfData] of Object.entries(sectionData as any)) {
        const attrs = getAttrs(tfData);
        if (!attrs) continue;
        catScopes.push(tf);
        const basePath = `${analysisKey}.${section}.${tf}`;
        getAllPaths(attrs, basePath).forEach(p => {
          if (selected.has(p)) catAnySelected = true;
          else catAllSelected = false;
        });
        if (customs.has(basePath)) catAnySelected = true;
      }

      if (!catAnySelected) continue;

      const hasCustInSection = Array.from(customs.keys()).some(k => k.startsWith(`${analysisKey}.${section}.`));

      // All standard attrs selected in this module → use wildcard for all scopes
      if (catAllSelected && !hasCustInSection) {
        const scopeObj: any = {};
        catScopes.forEach(s => { scopeObj[s] = { analytics_attributes: [WILDCARD_ATTR] }; });
        result[analysisKey][section] = scopeObj;
        continue;
      }

      result[analysisKey][section] = {};

      for (const [tf, tfData] of Object.entries(sectionData as any)) {
        const attrs = getAttrs(tfData);
        if (!attrs) continue;
        const basePath = `${analysisKey}.${section}.${tf}`;

        // Check scope-level selection (standard + custom)
        const allTfPaths = getAllPaths(attrs, basePath);
        const tfAnySelected = allTfPaths.some(p => selected.has(p));
        const hasCustomsInScope = customs.has(basePath) && (customs.get(basePath)?.length ?? 0) > 0;
        if (!tfAnySelected && !hasCustomsInScope) continue; // Omit scope entirely if nothing selected

        const allTfSelected = allTfPaths.length > 0 && allTfPaths.every(p => selected.has(p));
        const customsInScope = customs.get(basePath) || [];

        if (!tfAnySelected && hasCustomsInScope) {
          // Only custom attributes, no standard selected → just custom entries
          const customEntries = customsInScope.map(c => ({
            name: c.name, type: 'custom',
            ...(c.custom_attribute_logic ? { custom_attribute_logic: c.custom_attribute_logic } : {}),
            ...(c.definition ? { definition: c.definition } : {}),
          }));
          result[analysisKey][section][tf] = { analytics_attributes: customEntries };
        } else if (allTfSelected && customsInScope.length === 0) {
          // All standard attrs selected, no customs → wildcard
          result[analysisKey][section][tf] = { analytics_attributes: [WILDCARD_ATTR] };
        } else if (allTfSelected && customsInScope.length > 0) {
          // All standard + customs → wildcard + custom entries
          const customEntries = customsInScope.map(c => ({
            name: c.name, type: 'custom',
            ...(c.custom_attribute_logic ? { custom_attribute_logic: c.custom_attribute_logic } : {}),
            ...(c.definition ? { definition: c.definition } : {}),
          }));
          result[analysisKey][section][tf] = { analytics_attributes: [WILDCARD_ATTR, ...customEntries] };
        } else {
          // Specific attributes selected (mix of standard + custom)
          const filtered = filterSelected(attrs, selected, basePath, customs);
          if (filtered.length > 0) {
            result[analysisKey][section][tf] = { analytics_attributes: filtered };
          }
        }
      }

      if (Object.keys(result[analysisKey][section]).length === 0) delete result[analysisKey][section];
    }

    if (Object.keys(result[analysisKey]).length === 0) delete result[analysisKey];
  }

  return result;
};

/* ── Checkbox ── */
const Check: React.FC<{ checked: boolean; indeterminate?: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ checked, indeterminate, onChange, disabled }) => {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!(indeterminate && !checked);
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={e => !disabled && onChange(e.target.checked)}
      disabled={disabled}
      style={{
        width: 18, height: 18, cursor: disabled ? 'default' : 'pointer',
        accentColor: '#2563eb', flexShrink: 0,
      }}
    />
  );
};

/* ── Attribute row with checkbox ── */
const AttrRow: React.FC<{
  attr: AnalyticsAttribute; path: string; selected: Set<string>;
  onToggle: (path: string, checked: boolean) => void; depth?: number; isCustom?: boolean;
  onDeleteCustom?: () => void;
}> = ({ attr, path, selected, onToggle, depth = 0, isCustom, onDeleteCustom }) => {
  const [open, setOpen] = useState(false);
  const hasKids = !!(attr.attributes && attr.attributes.length > 0);
  const isChecked = selected.has(path);

  // Check if some but not all children are selected
  let childPaths: string[] = [];
  if (hasKids) childPaths = getAllPaths(attr.attributes, path);
  const someChildSelected = childPaths.some(p => selected.has(p));
  const allChildSelected = childPaths.length > 0 && childPaths.every(p => selected.has(p));
  const indeterminate = someChildSelected && !allChildSelected && !isChecked;

  return (
    <div style={{ paddingLeft: depth * 20 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
        borderBottom: '1px solid #f8fafc',
      }}>
        <Check checked={isChecked || allChildSelected} indeterminate={indeterminate} onChange={v => onToggle(path, v)} />
        {hasKids && (
          <div onClick={() => setOpen(!open)} style={{ cursor: 'pointer', display: 'flex' }}>
            {open ? <ChevronDown style={{ width: 14, height: 14, color: '#94a3b8' }} /> : <ChevronRight style={{ width: 14, height: 14, color: '#94a3b8' }} />}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#0f172a' }}>{attr.name}</span>
            {attr.data_type && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, backgroundColor: '#dbeafe', color: '#1e40af' }}>
                {attr.data_type}
              </span>
            )}
            {isCustom && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, backgroundColor: '#f5f3ff', color: '#7c3aed' }}>custom</span>
            )}
            {attr.type && attr.type !== 'value' && !isCustom && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, backgroundColor: '#f1f5f9', color: '#64748b' }}>{attr.type}</span>
            )}
          </div>
          {attr.definition && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{attr.definition}</div>}
        </div>
        {isCustom && onDeleteCustom && (
          <button onClick={onDeleteCustom} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}>
            <Trash2 style={{ width: 14, height: 14 }} />
          </button>
        )}
      </div>
      {hasKids && open && attr.attributes!.map(child => (
        <AttrRow key={child.name} attr={child} path={`${path}.${child.name}`} selected={selected} onToggle={onToggle} depth={depth + 1} />
      ))}
    </div>
  );
};

/* ── Main component ── */
/**
 * Extract selectedPaths and custom attributes from an existing analytics_attributes_configuration.
 * Walks the config tree: analysisKey → section → timeframe → { analytics_attributes: [...] }
 */
const extractFromConfig = (config: any): { paths: Set<string>; customs: Map<string, AnalyticsAttribute[]> } => {
  const paths = new Set<string>();
  const customs = new Map<string, AnalyticsAttribute[]>();
  if (!config || typeof config !== 'object') return { paths, customs };

  for (const [analysisKey, analysisData] of Object.entries(config)) {
    if (!analysisData || typeof analysisData !== 'object') continue;
    for (const [section, sectionData] of Object.entries(analysisData as any)) {
      if (!sectionData || typeof sectionData !== 'object') continue;
      for (const [tf, tfData] of Object.entries(sectionData as any)) {
        const attrs = (tfData as any)?.analytics_attributes;
        if (!attrs || !Array.isArray(attrs)) continue;
        const basePath = `${analysisKey}.${section}.${tf}`;
        for (const attr of attrs) {
          if (attr.name === '*') {
            // Wildcard — we'll select all standard attrs later when allAttributes loads
            paths.add(`${basePath}.__wildcard__`);
          } else if (attr.type === 'custom') {
            const existing = customs.get(basePath) || [];
            existing.push(attr);
            customs.set(basePath, existing);
            paths.add(`${basePath}.${attr.name}`);
          } else {
            paths.add(`${basePath}.${attr.name}`);
          }
        }
      }
    }
  }
  return { paths, customs };
};

const AttributeSelector: React.FC<AttributeSelectorProps> = React.memo(({ onSelectionChange, disabled: _disabled, clientName, instanceId, initialConfig }) => {
  const [allAttributes, setAllAttributes] = useState<AnalyticsAttributesResponse | null>(null);
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Custom attributes
  const [customAttributes, setCustomAttributes] = useState<Map<string, AnalyticsAttribute[]>>(new Map());
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customParentPath, setCustomParentPath] = useState('');
  const [customName, setCustomName] = useState('');
  const [customLogic, setCustomLogic] = useState('');
  const [groovyScript, setGroovyScript] = useState('');
  const [groovyDefinition, setGroovyDefinition] = useState('');
  const [isGeneratingGroovy, setIsGeneratingGroovy] = useState(false);
  const [groovyError, setGroovyError] = useState('');

  useEffect(() => {
    analyticsOnboardingAPI.getAttributes()
      .then(data => {
        setAllAttributes(data);

        // If initialConfig provided, pre-select paths
        if (initialConfig && data) {
          const { paths: initPaths, customs: initCustoms } = extractFromConfig(initialConfig);

          // Expand wildcards: replace __wildcard__ markers with all standard attr paths
          const expandedPaths = new Set<string>();
          for (const p of initPaths) {
            if (p.endsWith('.__wildcard__')) {
              const basePath = p.replace('.__wildcard__', '');
              // Walk allAttributes to find attrs at this basePath
              const parts = basePath.split('.');
              let node: any = data;
              for (const part of parts) {
                if (node && typeof node === 'object') node = node[part];
                else { node = null; break; }
              }
              const attrs = getAttrs(node);
              if (attrs) {
                getAllPaths(attrs, basePath).forEach(ap => expandedPaths.add(ap));
              }
            } else {
              expandedPaths.add(p);
            }
          }

          setSelectedPaths(expandedPaths);
          if (initCustoms.size > 0) setCustomAttributes(initCustoms);
        }
      })
      .catch(() => setError('Failed to load attributes'))
      .finally(() => setIsLoading(false));
  }, []);

  // Build config and notify parent
  const config = useMemo(() => {
    if (!allAttributes) return null;
    return buildConfig(allAttributes, selectedPaths, customAttributes);
  }, [allAttributes, selectedPaths, customAttributes]);

  useEffect(() => {
    if (config) onSelectionChange(config);
  }, [config, onSelectionChange]);

  const handleToggle = useCallback((path: string, checked: boolean) => {
    setSelectedPaths(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(path);
        // Auto-select parents
        const parts = path.split('.');
        for (let i = 3; i < parts.length; i++) {
          next.add(parts.slice(0, i).join('.'));
        }
      } else {
        // Deselect self and all children
        for (const p of prev) {
          if (p === path || p.startsWith(path + '.')) next.delete(p);
        }
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((basePath: string, attrs: AnalyticsAttribute[] | null) => {
    if (!attrs) return;
    const paths = getAllPaths(attrs, basePath);
    setSelectedPaths(prev => {
      const next = new Set(prev);
      paths.forEach(p => next.add(p));
      return next;
    });
  }, []);

  const clearAll = useCallback((basePath: string, attrs: AnalyticsAttribute[] | null) => {
    if (!attrs) return;
    const paths = getAllPaths(attrs, basePath);
    setSelectedPaths(prev => {
      const next = new Set(prev);
      paths.forEach(p => next.delete(p));
      return next;
    });
  }, []);

  const handleGenerateGroovy = useCallback(async () => {
    if (!customLogic.trim()) return;
    setIsGeneratingGroovy(true);
    setGroovyError('');
    setGroovyScript('');
    setGroovyDefinition('');
    try {
      const chatId = crypto.randomUUID?.() || Math.random().toString(36).slice(2);
      const metadata = { client_name: clientName || '', instance_id: instanceId || '', attribute_name: customName.trim() || '' };
      const result = await analyticsOnboardingAPI.generateGroovy(customLogic.trim(), chatId, metadata);
      // Response can be string or { script, definition, jira }
      if (result && typeof result === 'object' && result.script) {
        setGroovyScript(result.script.trim());
        if (result.definition) setGroovyDefinition(result.definition.trim());
      } else if (result && typeof result === 'string' && result.trim()) {
        setGroovyScript(result.trim());
      } else {
        setGroovyError('Groovy will be generated by tech team based on your logic');
      }
    } catch {
      setGroovyError('Groovy will be generated by tech team based on your logic');
    } finally {
      setIsGeneratingGroovy(false);
    }
  }, [customLogic, clientName, instanceId]);

  const addCustomAttribute = useCallback(() => {
    if (!customName.trim()) return;
    // If groovy script generated, use it as custom_attribute_logic and groovyDefinition as definition
    // If groovy failed/empty, use user's custom logic as definition, leave custom_attribute_logic empty
    const hasGroovy = !!(groovyScript && groovyScript.trim());
    const attr: AnalyticsAttribute = {
      name: customName.trim(), type: 'custom' as any,
      custom_attribute_logic: hasGroovy ? groovyScript.trim() : undefined,
      definition: hasGroovy ? (groovyDefinition || customLogic.trim() || undefined) : (customLogic.trim() || undefined),
      attributes: null,
    };
    setCustomAttributes(prev => {
      const next = new Map(prev);
      const existing = next.get(customParentPath) || [];
      next.set(customParentPath, [...existing, attr]);
      return next;
    });
    const fullPath = `${customParentPath}.${customName.trim()}`;
    setSelectedPaths(prev => new Set(prev).add(fullPath));
    setCustomName('');
    setCustomLogic('');
    setGroovyScript('');
    setGroovyDefinition('');
    setGroovyError('');
    setShowAddCustom(false);
  }, [customName, customLogic, groovyScript, groovyDefinition, customParentPath]);

  const removeCustom = useCallback((parentPath: string, attrName: string) => {
    setCustomAttributes(prev => {
      const next = new Map(prev);
      const existing = next.get(parentPath) || [];
      next.set(parentPath, existing.filter(a => a.name !== attrName));
      if (next.get(parentPath)!.length === 0) next.delete(parentPath);
      return next;
    });
    setSelectedPaths(prev => {
      const next = new Set(prev);
      next.delete(`${parentPath}.${attrName}`);
      return next;
    });
  }, []);

  const toggleSection = (key: string) => {
    setExpandedSections(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const selectedCount = selectedPaths.size;

  const sectionMatchesSearch = (sectionName: string, sectionData: any, _analysisKey: string): boolean => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (sectionName.toLowerCase().includes(q)) return true;
    for (const [, tfData] of Object.entries(sectionData as any)) {
      const attrs = getAttrs(tfData);
      if (!attrs) continue;
      const matchInAttrs = (list: AnalyticsAttribute[]): boolean =>
        list.some(a => a.name.toLowerCase().includes(q) || (a.attributes ? matchInAttrs(a.attributes) : false));
      if (matchInAttrs(attrs)) return true;
    }
    return false;
  };

  if (isLoading) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
        Loading attributes...
      </div>
    );
  }

  if (error || !allAttributes) {
    return <div style={{ padding: '20px 0', color: '#ef4444', fontSize: 13 }}>{error || 'No attributes available'}</div>;
  }

  const renderSection = (analysisKey: string, analysisData: Record<string, any>, icon: React.ReactNode, title: string) => (
    <div key={analysisKey} style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {icon}
        <span style={{ fontSize: 14, color: '#0f172a' }}><b>{title}</b></span>
      </div>

      {Object.entries(analysisData).filter(([sectionName, sectionData]) =>
        sectionMatchesSearch(sectionName, sectionData, analysisKey)
      ).map(([sectionName, sectionData]) => {
        if (!sectionData || typeof sectionData !== 'object') return null;
        const sectionKey = `${analysisKey}.${sectionName}`;
        const isExpanded = expandedSections.has(sectionKey) || !!searchQuery;

        return (
          <div key={sectionName} style={{
            border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8, overflow: 'hidden',
          }}>
            <div
              onClick={() => toggleSection(sectionKey)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', cursor: 'pointer', backgroundColor: isExpanded ? '#f8fafc' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ChevronDown style={{
                  width: 16, height: 16, color: '#94a3b8',
                  transform: isExpanded ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform 0.2s',
                }} />
                <span style={{ fontSize: 13, color: '#334155' }}><b>{sectionName.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim()}</b></span>
              </div>
            </div>

            {isExpanded && (
              <div style={{ borderTop: '1px solid #f1f5f9' }}>
                {Object.entries(sectionData as any).map(([tf, tfData]: [string, any]) => {
                  const attrs = getAttrs(tfData);
                  if (!attrs || attrs.length === 0) return null;
                  const basePath = `${analysisKey}.${sectionName}.${tf}`;
                  const tfKey = `${sectionKey}.${tf}`;
                  const isTfExpanded = expandedSections.has(tfKey) || !!searchQuery;
                  const customs = customAttributes.get(basePath) || [];

                  return (
                    <div key={tf} style={{ borderTop: '1px solid #f8fafc' }}>
                      <div
                        onClick={() => toggleSection(tfKey)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 16px 10px 32px', cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ChevronRight style={{
                            width: 14, height: 14, color: '#cbd5e1',
                            transform: isTfExpanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.15s',
                          }} />
                          <span style={{ fontSize: 12, color: '#64748b', textTransform: 'capitalize' }}>{tf}</span>
                          <span style={{ fontSize: 11, color: '#cbd5e1' }}>({attrs.length})</span>
                        </div>
                        {isTfExpanded && (
                          <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                            <button onClick={() => selectAll(basePath, attrs)} style={{ fontSize: 11, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}>Select all</button>
                            <span style={{ color: '#e2e8f0' }}>|</span>
                            <button onClick={() => clearAll(basePath, attrs)} style={{ fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>
                            <span style={{ color: '#e2e8f0' }}>|</span>
                            <button onClick={() => { setCustomParentPath(basePath); setShowAddCustom(true); }} style={{ fontSize: 11, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer' }}>+ Custom</button>
                          </div>
                        )}
                      </div>

                      {isTfExpanded && (
                        <div style={{ padding: '0 16px 12px 48px' }}>
                          {attrs.filter(a => !searchQuery || a.name.toLowerCase().includes(searchQuery.toLowerCase())).map(attr => (
                            <AttrRow
                              key={attr.name} attr={attr} path={`${basePath}.${attr.name}`}
                              selected={selectedPaths} onToggle={handleToggle}
                            />
                          ))}
                          {/* Custom attributes */}
                          {customs.map(ca => (
                            <AttrRow
                              key={ca.name} attr={ca} path={`${basePath}.${ca.name}`}
                              selected={selectedPaths} onToggle={handleToggle} isCustom
                              onDeleteCustom={() => removeCustom(basePath, ca.name)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, color: '#0f172a' }}><b>Select Attributes</b></div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            {selectedCount > 0 ? `${selectedCount} attribute${selectedCount !== 1 ? 's' : ''} selected` : 'Choose attributes to include in your analytics'}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="equal-ds-root" style={{ marginBottom: 24 }}>
        <SearchBar
          variant="simple"
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Filter attributes..."
        />
      </div>

      {/* Select All / Clear All */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => {
            if (!allAttributes) return;
            const allPaths = new Set<string>();
            const collectAll = (analysis: any, prefix: string) => {
              if (!analysis) return;
              for (const [section, sData] of Object.entries(analysis)) {
                if (!sData || typeof sData !== 'object') continue;
                for (const [tf, tfData] of Object.entries(sData as any)) {
                  const attrs = getAttrs(tfData);
                  if (attrs) getAllPaths(attrs, `${prefix}.${section}.${tf}`).forEach(p => allPaths.add(p));
                }
              }
            };
            for (const [key, val] of Object.entries(allAttributes)) {
              if (val && typeof val === 'object') collectAll(val, key);
            }
            setSelectedPaths(allPaths);
          }}
          style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}
        >
          <b>Select All</b>
        </button>
        <span style={{ color: '#e2e8f0' }}>|</span>
        <button
          onClick={() => setSelectedPaths(new Set())}
          style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}
        >
          <b>Clear All</b>
        </button>
      </div>

      {/* Sections */}
      <div style={{ maxHeight: 500, overflowY: 'auto' }}>
        {allAttributes.overall_analysis && renderSection(
          'overall_analysis', allAttributes.overall_analysis,
          <Layers style={{ width: 16, height: 16, color: '#2563eb' }} />, 'Overall Analysis'
        )}
        {allAttributes.account_wise_analysis && renderSection(
          'account_wise_analysis', allAttributes.account_wise_analysis,
          <Database style={{ width: 16, height: 16, color: '#059669' }} />, 'Account-wise Analysis'
        )}
      </div>

      {/* Add Custom Attribute Dialog */}
      {showAddCustom && (
        <>
          <div onClick={() => { setShowAddCustom(false); setGroovyScript(''); setGroovyDefinition(''); setGroovyError(''); }} style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.2)', zIndex: 9998,
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 460, backgroundColor: '#fff', borderRadius: 16, padding: '28px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)', zIndex: 9999, maxHeight: '80vh', overflowY: 'auto',
          }}>
            <div style={{ fontSize: 16, color: '#0f172a', marginBottom: 4 }}><b>Add Custom Attribute</b></div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
              Adding to: <span style={{ color: '#475569' }}>{customParentPath.split('.').slice(-1)[0]}</span>
            </div>

            <div className="equal-ds-root" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <InputField
                label="Attribute Name"
                value={customName}
                onChange={e => setCustomName(e.target.value)}
                placeholder="e.g., custom_ratio_metric"
              />
              <InputField
                label="Custom Logic"
                value={customLogic}
                onChange={e => { setCustomLogic(e.target.value); setGroovyScript(''); setGroovyDefinition(''); setGroovyError(''); }}
                placeholder="e.g., Get the transaction amount"
              />
            </div>

            {/* Generate Groovy */}
            {customLogic.trim() && (
              <div style={{ marginTop: 16 }}>
                <div className="equal-ds-root">
                  <Button
                    variant="secondary" size="sm"
                    onClick={handleGenerateGroovy}
                    disabled={isGeneratingGroovy || !customLogic.trim()}
                  >
                    {isGeneratingGroovy ? 'Generating...' : 'Generate Groovy Script'}
                  </Button>
                </div>

                {groovyScript && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: '#059669', marginBottom: 6 }}><b>Generated Groovy Script</b></div>
                    <div style={{
                      padding: '12px 14px', borderRadius: 10, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
                      fontSize: 13, fontFamily: "'SF Mono', ui-monospace, monospace", color: '#166534',
                      wordBreak: 'break-all',
                    }}>
                      {groovyScript}
                    </div>
                  </div>
                )}

                {groovyDefinition && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: '#2563eb', marginBottom: 6 }}><b>Generated Definition</b></div>
                    <div style={{
                      padding: '12px 14px', borderRadius: 10, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
                      fontSize: 13, color: '#1e40af', lineHeight: 1.5,
                    }}>
                      {groovyDefinition}
                    </div>
                  </div>
                )}

                {groovyError && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{
                      padding: '10px 14px', borderRadius: 10,
                      backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                      fontSize: 12, color: '#92400e',
                    }}>
                      {groovyError}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ height: 32 }} />
            <div className="equal-ds-root" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="secondary" size="sm" onClick={() => { setShowAddCustom(false); setCustomName(''); setCustomLogic(''); setGroovyScript(''); setGroovyDefinition(''); setGroovyError(''); }}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={addCustomAttribute} disabled={!customName.trim() || !customLogic.trim() || (!groovyScript && !groovyError)}>
                Add Attribute
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export default AttributeSelector;
